import React, { FC, useCallback } from 'react';
import withStyles from 'src/common/withStyles';
import { Form } from 'react-final-form';
import { Helmet } from 'react-helmet-async';
import { Link, useHistory } from 'react-router-dom';

import { PATHS } from '../../../../common/constants';
import FormError from '../../../../common/components/FormError';
import { rffSubmitResponse } from '../../../../common/helpers';
import { BackButton } from '../../../../common/components/BackButton';
import EmailField from '../../../../common/components/fields/EmailField';
import Button from '../../../../common/components/Button';
import { combineValidators, rffValidators } from '../../../../common/validation';
import apiFetch from '../../../../core/fetch';
import useGrecaptcha from '../../../hooks/useGrecaptcha';
import s from './ForgotPasswordSend.module.scss';

export const asyncEmailMessage = "We don't have that email in our system.";

export const asyncEmailValidate = async (email: string) => {
  const response = await apiFetch('/auth/validate/registration/', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  if (response.status === 400) {
    // This is here rather than above for the case of a 500
    // with no body
    const data = await response.json();
    if (data.email) {
      // If we get the "hey, that's already an email address"
      // message, we're good.
      return undefined;
    }
    return asyncEmailMessage;
  }
  if (!response.ok) {
    // If the response is otherwise not OK, we want to pass validation,
    // since we can't make a determination about whether the validation
    // was good.
    return undefined;
  }
  // Else that email didn't exist
  return asyncEmailMessage;
};
const validate = combineValidators([
  rffValidators.required({ message: 'An email is required.' }),
  rffValidators.email({ message: "That doesn't look like a valid email." }),
  asyncEmailValidate,
]);

const title = 'Forgot your Password?';

const resetPasswordCall = async (values: Record<string, string>) => (
  apiFetch('/auth/password/reset/', {
    method: 'POST',
    body: JSON.stringify(values),
  })
);

const ForgotPassword: FC = () => {
  const { goBack, push } = useHistory();

  const resetPassword = useCallback(
    (values: Record<string, string>) => resetPasswordCall(values).then(
      rffSubmitResponse(() => push(PATHS.PASSWORD_FORGOT_SUBMITTED)),
    ),
    [push],
  );

  const onSubmit = useGrecaptcha('password_reset', resetPassword);

  return (
    <div className={s.container}>
      <Helmet>
        <title lang="en">{title}</title>
      </Helmet>

      <div className={s.intro}>
        <h1 className={s.title}>{title}</h1>
        <div className={s.message}>
          <p>{"Not a problem! We'll email you a reset link."}</p>
        </div>
      </div>

      <Form
        onSubmit={onSubmit}
        validateOnBlur
      >
        {({ handleSubmit, submitting, submitError }) => (
          <form
            method="POST"
            onSubmit={handleSubmit}
            className={s.form}
          >
            <FormError error={submitError} />
            <EmailField
              name="email"
              autoFocus
              className={s.input}
              validate={validate}
            />

            <div className={s.actionGroup}>
              <Button
                type="submit"
                submitting={submitting}
                fullWidth
              >
                Send Email
              </Button>
              <div className={s.account}>
                {"Don't have an account? "}
                <Link
                  to={PATHS.REGISTER}
                  className={s.link}
                >
                  Create Account.
                </Link>
              </div>
            </div>
          </form>
        )}
      </Form>
      <BackButton onClick={goBack} />
    </div>
  );
};

export default withStyles(s)(ForgotPassword);
